import type { CartLine, CartModifierGroupStored } from "@/store/cart/cart.types"
import type { LineCombo } from "@/shared/cart/model.types"
import { roundMoney } from "@/domain/money/round-money"

/**
 * Canonical modifier groups for stable equality / merge keys.
 */
export function canonicalizeModifierGroups(
  groups: CartModifierGroupStored[] | undefined | null
): CartModifierGroupStored[] {
  const raw = groups ?? []
  if (raw.length === 0) return []

  const out: CartModifierGroupStored[] = []
  for (const g of raw) {
    const gid = String(g?.modifier_group_id ?? "").trim()
    if (!gid) continue
    const items = (g.items ?? [])
      .filter((m) => String(m?.modifier_group_item_id ?? "").trim() !== "")
      .map((m) => ({
        modifier_group_item_id: String(m.modifier_group_item_id).trim(),
        child_menu_item_id: String(m.child_menu_item_id ?? "").trim(),
        quantity: m.quantity,
        // Currency, not an integer count — must preserve fractional prices (e.g. £1.50).
        // Matches the existing roundMoney convention already used for this same field in
        // domain/reorder/reorder.transform.ts's own canonicalizeModifierGroups.
        delta_price: roundMoney(Number(m.delta_price) || 0),
      }))
      .sort((a, b) => {
        const c = a.modifier_group_item_id.localeCompare(b.modifier_group_item_id)
        if (c !== 0) return c
        return a.child_menu_item_id.localeCompare(b.child_menu_item_id)
      })
    if (items.length === 0) continue
    out.push({ modifier_group_id: gid, items })
  }
  out.sort((a, b) => a.modifier_group_id.localeCompare(b.modifier_group_id))
  return out
}

function combosKey(combos: LineCombo[] | undefined): string {
  return (combos ?? [])
    .map((x) => `${x.combo_group_id}:${x.child_menu_item_id}`)
    .sort()
    .join(",")
}

function groupsFingerprint(groups: CartModifierGroupStored[]): string {
  const c = canonicalizeModifierGroups(groups)
  return c
    .map((g) =>
      [
        g.modifier_group_id,
        ...g.items.map(
          (m) =>
            `${m.modifier_group_item_id}:${m.child_menu_item_id}:${String(m.quantity ?? "")}:${m.delta_price}`
        ),
      ].join(":")
    )
    .join("|")
}

export function isSameCartLine(a: CartLine, b: CartLine): boolean {
  const aRid = String(a.restaurant_id ?? "").trim()
  const bRid = String(b.restaurant_id ?? "").trim()
  if (aRid && bRid && aRid !== bRid) return false
  if (String(a.menu_item_id) !== String(b.menu_item_id)) return false
  if (combosKey(a.selectedCombos) !== combosKey(b.selectedCombos)) return false
  return groupsFingerprint(a.selected_modifiers) === groupsFingerprint(b.selected_modifiers)
}
